import { trpc } from 'src/trpc';
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { zodResolver } from '@hookform/resolvers/zod';
import { customerZ } from '@libs/schemas';
import { Button } from '@/components/ui/button';
import { FormField, FormItem, FormLabel, FormControl, Form } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Loader } from 'components/Loader';
import { z } from 'zod';
import { useToast } from 'components/useToast';
import { PhoneFormField } from 'components/ui/PhoneInput';
import { PHONE_REGEX, isValidPhoneNumber } from '@libs/schemas';
import { useForm } from 'react-hook-form';

interface CustomerEditSheetProps {
  isOpen: boolean;
  onClose: () => void;
  customerId: string;
  refetchCustomers: () => void;
}

const customerUpdateSchema = customerZ
  .omit({ smsNotificationOptIn: true }) // Remove smsNotificationOptIn
  .extend({
    email: z.email(), // Make email required
    phone: z
      .string()
      .regex(PHONE_REGEX, 'Invalid phone number format. Must be +1 followed by 10 digits')
      .refine(isValidPhoneNumber, 'Please enter a valid US phone number'), // Keep phone required with proper validation
  });

export const CustomerEditSheet = ({
  isOpen,
  onClose,
  customerId,
  refetchCustomers,
}: CustomerEditSheetProps) => {
  const {
    data: customer,
    refetch: refetchCustomer,
    isLoading: userInfoLoading,
  } = trpc.admin.customer.adminGetCustomer.useQuery(customerId, {
    enabled: isOpen && !!customerId,
  });

  const handleCustomerUpdateSuccess = () => {
    refetchCustomer();
    refetchCustomers();
  };

  const { errorToast } = useToast();
  const { mutate, isPending } = trpc.admin.customer.adminUpdateCustomer.useMutation({
    onSuccess: handleCustomerUpdateSuccess,
    onError: (error: { message?: string }) => {
      errorToast({
        title: 'Failed to update customer',
        description:
          error.message || 'Please try again or contact support if the problem persists.',
      });
    },
  });

  const form = useForm({
    resolver: zodResolver(customerUpdateSchema),
    values: customer
      ? {
          email: customer.email || '',
          name: {
            first: customer.name?.first || '',
            last: customer.name?.last || '',
          },
          phone: customer.phone || '',
          company: customer.company || { name: undefined, email: undefined },
        }
      : undefined,
  });

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="p-0 sm:max-w-[600px] lg:max-w-[700px] xl:max-w-[800px] h-full flex flex-col justify-between">
        <SheetHeader className="border-b bg-white px-6 py-4">
          <SheetTitle className="text-left text-lg font-semibold text-gray-900">
            Edit Customer
          </SheetTitle>
        </SheetHeader>

        <div className="flex flex-col gap-6 flex-grow flex-grow">
          {userInfoLoading ? (
            <Loader title="Gathering your info" />
          ) : isPending ? (
            <Loader title="Updating your info" />
          ) : (
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit((v) =>
                  mutate({
                    ...v,
                    name: { first: v.name.first, last: v.name.last },
                    customerId,
                    userId: customer!.userId,
                    email: v.email || '',
                    phone: v.phone || '',
                    company: {
                      name: v.company?.name || undefined,
                      email: v.company?.email || undefined,
                    },
                  }),
                )}
                className="h-full w-full flex flex-col flex-grow"
              >
                <div className="w-full justify-center flex flex-col gap-4 flex-grow justify-between px-6">
                  <div className="grid grid-cols-2 gap-8 justify-start w-full">
                    <FormField
                      control={form.control}
                      name="name.first"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="label">First Name</FormLabel>
                          <FormControl>
                            <Input placeholder="First Name" {...field} />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="name.last"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="label">Last Name</FormLabel>
                          <FormControl>
                            <Input placeholder="Last Name" {...field} />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="label">Email</FormLabel>
                          <FormControl>
                            <Input placeholder="Email" {...field} />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <PhoneFormField
                      control={form.control}
                      name="phone"
                      label="Phone Number"
                      placeholder="+1 (555) 123-4567"
                    />

                    <FormField
                      control={form.control}
                      name="company.name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="label">Business Name</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Enter business name (optional)"
                              {...field}
                              value={field.value || ''}
                              onChange={(e) => field.onChange(e.target.value || undefined)}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="company.email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="label">Business Email</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Enter business email (optional)"
                              {...field}
                              value={field.value || ''}
                              onChange={(e) => field.onChange(e.target.value || undefined)}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                <SheetFooter className="border-t bg-gray-50 px-6 py-4">
                  <div className="flex w-full justify-end gap-4">
                    <Button disabled={isPending || !customer} type="submit" className="text-white">
                      Save Changes
                    </Button>
                  </div>
                </SheetFooter>
              </form>
            </Form>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
